import { useEffect, useState } from "react";
import { ButtonPopup } from "../ui";
import { useNavigate } from "react-router-dom";
import { useAuthToken } from "@/hooks/useAuthToken";
import Alerta from "../alert/Alerta";

const Backoffice: React.FC = () => {
  const navigate = useNavigate();

  // 🔥 RBAC REAL
  const { roles, clearToken } = useAuthToken();

  const [confirmLogout, setConfirmLogout] = useState(false);
  const [alerta, setAlerta] = useState<{
    mensaje: string;
    type: "success" | "error" | "warning";
  } | null>(null);

  // 🛡️ Normalización segura
  const rolesClean = Array.isArray(roles)
    ? roles
        .filter((r) => typeof r === "string" && r.trim() !== "")
        .map((r) => r.toLowerCase().trim().replace(/\s|_/g, ""))
    : [];

  const isSuperAdmin = rolesClean.includes("superadmin");

  const handleSuperAdminClick = () => {
    if (isSuperAdmin) {
      navigate("/superAdmin");
    } else {
      setAlerta({
        mensaje: "Necesitas permisos de SuperAdmin",
        type: "error",
      });

      setTimeout(() => setAlerta(null), 3000);
    }
  };

  useEffect(() => {
    return () => {
      setConfirmLogout(false);
      setAlerta(null);
      window.scrollTo(0, 0);
    };
  }, []);

  return (
    <>
      {/* ALERTA NORMAL */}
      {alerta && (
        <Alerta
          mensaje={alerta.mensaje}
          type={alerta.type}
          onClose={() => setAlerta(null)}
        />
      )}

      {/* ALERTA CONFIRMAR LOGOUT */}
      {confirmLogout && (
        <Alerta
          mensaje="¿Estás seguro de que deseas cerrar sesión?"
          type="warning"
          autoProgress={false}
          onClose={() => setConfirmLogout(false)}
        >
          <div className="flex justify-end gap-3 mt-4">
            <ButtonPopup
              onClick={() => setConfirmLogout(false)}
              className="btn-base bg-zinc-300 hover:bg-zinc-400"
            >
              Cancelar
            </ButtonPopup>

            <ButtonPopup
              onClick={() => {
                clearToken();
                setConfirmLogout(false);
                navigate("/", { replace: true });
              }}
              className="btn-base bg-red-600 hover:bg-red-700 text-white"
            >
              Salir
            </ButtonPopup>
          </div>
        </Alerta>
      )}

      <div className="relative w-full min-h-screen bg-zinc-900 overflow-hidden">
        {/* LOGO FONDO */}
        <div
          className="
            absolute inset-0
            bg-[url('/img/logo/LogoLinkServs.png')]
            bg-center bg-no-repeat bg-contain
            pointer-events-none
          "
        />

        {/* CONTENIDO */}
        <div className="relative z-10 h-full flex flex-col p-4 sm:p-6">
          {/* HEADER */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4 pt-20 md:pt-4 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold text-center sm:text-left">
              Backoffice LinkServs
            </h1>
          </div>

          {/* GRID PRINCIPAL */}
          <div className="flex-1 flex justify-center items-start md:items-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-7xl">
              {/* SECCIONES */}
              <div className="flex flex-col gap-4 items-center justify-center">
                <span className="btn-base btn-water border-2 border-green-700 rounded-full px-8 py-3 h-32 w-32 flex items-center justify-center text-center">
                  Secciones
                </span>
                <ButtonPopup
                  onClick={() => navigate("/insertarSecciones")}
                  className="btn-base hover:bg-green-800 border-green-700 btn-water"
                >
                  Insertar Secciones
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/editarSecciones")}
                  className="btn-base hover:bg-green-800 border-green-700 btn-water"
                >
                  Editar Secciones
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/borrarSecciones")}
                  className="btn-base hover:bg-green-800 border-green-700 btn-water"
                >
                  Borrar Secciones
                </ButtonPopup>
              </div>

              {/* PROFESIONES */}
              <div className="flex flex-col gap-4 items-center justify-center">
                <span className="btn-base btn-water border-2 border-yellow-600 rounded-full px-8 py-3 h-32 w-32 flex items-center justify-center text-center">
                  Profesiones
                </span>
                <ButtonPopup
                  onClick={() => navigate("/insertarProfesiones")}
                  className="btn-base hover:bg-yellow-600 border-yellow-600 btn-water"
                >
                  Insertar Profesiones
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/editarProfesion")}
                  className="btn-base hover:bg-yellow-600 border-yellow-600 btn-water"
                >
                  Editar Profesiones
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/borrarProfesiones")}
                  className="btn-base hover:bg-yellow-600 border-yellow-600 btn-water"
                >
                  Borrar Profesiones
                </ButtonPopup>
              </div>

              {/* PROFESIONALES */}
              <div className="flex flex-col gap-4 items-center justify-center">
                <span className="btn-base btn-water border-2 border-blue-700 rounded-full px-8 py-3 h-32 w-32 flex items-center justify-center text-center">
                  Profesionales
                </span>
                <ButtonPopup
                  onClick={() => navigate("/buscarProfesionales")}
                  className="btn-base hover:bg-blue-700 border-blue-700 btn-water"
                >
                  Buscar Profesionales
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/editar-Profesional")}
                  className="btn-base hover:bg-blue-700 border-blue-700 btn-water"
                >
                  Editar Profesionales
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/bajaProfesional")}
                  className="btn-base hover:bg-blue-700 border-blue-700 btn-water"
                >
                  Baja Profesionales
                </ButtonPopup>
              </div>

              {/* USUARIOS */}
              <div className="flex flex-col gap-4 items-center justify-center">
                <span className="btn-base btn-water border-2 border-purple-700 rounded-full px-8 py-3 h-32 w-32 flex items-center justify-center text-center">
                  Usuarios
                </span>
                <ButtonPopup
                  onClick={() => navigate("/buscarUsuarioAdmin")}
                  className="btn-base hover:bg-purple-600 border-purple-700 btn-water"
                >
                  Buscar Usuarios
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/editarUsuarioAdmin")}
                  className="btn-base hover:bg-purple-600 border-purple-700 btn-water"
                >
                  Editar Usuarios
                </ButtonPopup>
                <ButtonPopup
                  onClick={() => navigate("/gestionUsuarioAdmin")}
                  className="btn-base hover:bg-purple-600 border-purple-700 btn-water"
                >
                  Borrar Usuarios
                </ButtonPopup>
              </div>
              {/* DONACIONES */}
              {isSuperAdmin && (
                <div className="flex flex-col gap-4 items-center justify-center">
                  <span className="btn-base btn-water border-2 border-emerald-600 rounded-full px-8 py-3 h-32 w-32 flex items-center justify-center text-center">
                    Donaciones
                  </span>

                  <ButtonPopup
                    onClick={() => navigate("/gestionDonaciones")}
                    className="btn-base hover:bg-emerald-700 border-emerald-600 btn-water"
                  >
                    Gestionar Donaciones
                  </ButtonPopup>
                </div>
              )}
            </div>
          </div>

          {/* BOTONES INFERIORES */}
          <div className="mt-6 md:mt-4 flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-7xl">
              <ButtonPopup
                onClick={() => navigate("/contratosResumen")}
                className="btn-base hover:bg-sky-600 h-32 w-full md:w-auto flex items-center justify-center text-center border-sky-700 btn-water"
              >
                Contratos
              </ButtonPopup>

              <ButtonPopup
                onClick={() => navigate("/facturasResumen")}
                className="btn-base hover:bg-cyan-600 h-32 w-full md:w-auto flex items-center justify-center text-center border-teal-700 btn-water"
              >
                Facturas
              </ButtonPopup>

              <ButtonPopup
                onClick={handleSuperAdminClick}
                className="btn-base hover:bg-fuchsia-600 h-32 w-full md:w-auto flex items-center justify-center text-center border-fuchsia-700 btn-water"
              >
                SuperAdmin
              </ButtonPopup>

              <ButtonPopup
                onClick={() => navigate("/afiliados")}
                className="btn-base hover:bg-orange-600 h-32 w-full md:w-auto flex items-center justify-center text-center border-orange-700 btn-water"
              >
                Afiliados y Códigos
              </ButtonPopup>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Backoffice;
